import React, { useEffect, useRef, useState } from "react";
import { EN } from "../../../Languages/EN";
import { DE } from "../../../Languages/DE";
import LanguageContext from "../../../helper/LanguageContext";
import { $crud } from "../../../helper/CrudFactory";
import { Form } from "react-bootstrap";
import { FaTimes } from "react-icons/fa";
import MaterialIcon from "../../../components/MaterialIcon";
import { IoClose } from "react-icons/io5";
import { KJUUP_COLORS, BASE_URL } from "../../../helper/constants";
import {
  DOC_ACCEPT_ATTRIBUTE,
  DOC_EXTENSIONS,
  MAX_DOCUMENTS,
  formatDocSize,
  getDocAccentColor,
  getDocExtension,
  getDocIconName,
  getDocKind,
  isDocOversized,
} from "../../../utils/documentAlbum";
import ExcelPreview from "../../../components/ExcelPreview";
import "./FloatingAction.css";
import {
  PHOTO_VIDEO_ACCEPT_ATTRIBUTE,
  VIDEO_EXTENSIONS,
  formatVideoDuration,
  isPhotoFile,
  isVideoFile,
  prepareChatVideo,
} from "../../../utils/chatVideo";
import { htmlToMarkdown as sharedHtmlToMarkdown, pasteAsPlainText, handleComposerDrop, getDroppedFiles, fileFingerprint } from "../../../utils/composerUtils";
import ChatShareSheet from "../../../components/chat/share/ChatShareSheet";
import { requestLocationMode } from "../../Chat/ChatMap/locationMode";

export default function FloatingAction({
  handleClose,
  showLocationDialog,
  sendImageMessage,
  sendVideoMessage,
  sendDocFile,
  sendDocFiles,
  setParam,
  chat,
  dropfiles,
  isInternal,
  messagePaar,
  userTyping,
  onBlur,
  onCreatePoll,
  canSendMedia = true,
  mentionSuggestions = [],
  // Optional (additive): the room for the Task tile ({ id, name }) and an owner handler for it.
  room = null,
  onCreateTask,
}) {
  const imageInputRef = useRef(null);
  const addMoreInputRef = useRef(null);
  const docInputRef = useRef(null);
  const docAddMoreInputRef = useRef(null);
  const { isGerm, strings } = React.useContext(LanguageContext);
  const [image, setImage] = useState([]);
  const [docs, setDocs] = useState([]);
  const [docActiveIndex, setDocActiveIndex] = useState(0);
  const [video, setVideo] = useState("");
  const [imageDimension, setImageDimension] = useState({});
  const [activeIndex, setActiveIndex] = useState(0);
  const [isClosing, setIsClosing] = useState(false);
  const [isPhotoWysiwygOpen, setIsPhotoWysiwygOpen] = useState(true);
  const [photoActiveFormats, setPhotoActiveFormats] = useState({});
  const photoRichEditorRef = useRef(null);
  const pendingPhotoHtmlRef = useRef(null);
  const [photoMentionInput, setPhotoMentionInput] = useState("");
  const [photoShowSuggestions, setPhotoShowSuggestions] = useState(false);
  const photoMentionCursorRef = useRef(null);
  const photoEditorInitRef = useRef(false);
  const [isDocDragOver, setIsDocDragOver] = useState(false);
  const docDragDepthRef = useRef(0);
  const lastDropfilesRef = useRef(null);

  useEffect(() => {
    if (!isPhotoWysiwygOpen) return;
    const editor = photoRichEditorRef.current;
    const pending = pendingPhotoHtmlRef.current;
    const editorEmpty = !editor || !String(editor.innerText || "").replace(/\u00A0/g, " ").trim();
    if (editor && pending && editorEmpty) {
      editor.innerHTML = pending;
      try {
        const range = document.createRange();
        range.selectNodeContents(editor);
        range.collapse(false);
        const sel = window.getSelection();
        sel.removeAllRanges();
        sel.addRange(range);
      } catch (e) { /* ignore */ }
    }
  }, [isPhotoWysiwygOpen]);

  const updatePhotoActiveFormats = React.useCallback(() => {
    if (!isPhotoWysiwygOpen) return;
    try {
      setPhotoActiveFormats({
        bold: document.queryCommandState("bold"),
        italic: document.queryCommandState("italic"),
        underline: document.queryCommandState("underline"),
        strikethrough: document.queryCommandState("strikeThrough"),
        unordered: document.queryCommandState("insertUnorderedList"),
        ordered: document.queryCommandState("insertOrderedList"),
      });
    } catch (e) { /* ignore */ }
  }, [isPhotoWysiwygOpen]);

  useEffect(() => {
    if (!isPhotoWysiwygOpen) return;
    document.addEventListener("selectionchange", updatePhotoActiveFormats);
    return () => document.removeEventListener("selectionchange", updatePhotoActiveFormats);
  }, [isPhotoWysiwygOpen, updatePhotoActiveFormats]);

  const photoHtmlToMarkdown = React.useCallback((html) => sharedHtmlToMarkdown(html), []);

  const getPhotoRichContent = React.useCallback(() => {
    const editor = photoRichEditorRef.current;
    if (!editor) return "";
    return photoHtmlToMarkdown(editor.innerHTML);
  }, [photoHtmlToMarkdown]);

  // Prefer the caption editor, but keep whatever the user already typed in the
  // main composer if the editor is empty (e.g. after a remount).
  const resolveCaption = React.useCallback(() => {
    if (isPhotoWysiwygOpen && photoRichEditorRef.current) {
      const fromEditor = getPhotoRichContent();
      if (fromEditor && String(fromEditor).trim()) return fromEditor;
    }
    return messagePaar || "";
  }, [isPhotoWysiwygOpen, getPhotoRichContent, messagePaar]);

  const applyPhotoFormat = React.useCallback((formatType) => {
    const editor = photoRichEditorRef.current;
    if (!editor || !isPhotoWysiwygOpen) return;
    editor.focus();
    switch (formatType) {
      case "bold": document.execCommand("bold", false, null); break;
      case "italic": document.execCommand("italic", false, null); break;
      case "underline": document.execCommand("underline", false, null); break;
      case "strikethrough": document.execCommand("strikeThrough", false, null); break;
      case "unordered": document.execCommand("insertUnorderedList", false, null); break;
      case "ordered": document.execCommand("insertOrderedList", false, null); break;
      default: break;
    }
  }, [isPhotoWysiwygOpen]);

  const photoMarkdownToHtml = React.useCallback((md) => {
    if (!md) return "";
    let html = md;
    html = html.replace(/^((?:- .+(?:\n|$))+)/gm, (block) => {
      const items = block.trim().split('\n').map(line => `<li>${line.replace(/^- /, '')}</li>`).join('');
      return `<ul>${items}</ul>`;
    });
    html = html.replace(/^((?:\d+\. .+(?:\n|$))+)/gm, (block) => {
      const items = block.trim().split('\n').map(line => `<li>${line.replace(/^\d+\.\s*/, '')}</li>`).join('');
      return `<ol>${items}</ol>`;
    });
    html = html.replace(/\*\*([\s\S]*?)\*\*/g, '<b>$1</b>');
    html = html.replace(/~~([\s\S]*?)~~/g, '<del>$1</del>');
    html = html.replace(/@\[([^\]]*)\]\(([^)]*)\)/g, '<span class="mention-tag" data-mention-id="$2" data-mention-name="$1" contenteditable="false">@$1</span>');
    html = html.replace(/\[([^\]]*)\]\(([^)]*)\)/g, '<a href="$2">$1</a>');
    html = html.replace(/\n/g, '<br>');
    return html;
  }, []);

  // Keep the last non-empty composer snapshot so an empty caption remount
  // cannot wipe the text the user already typed.
  const messagePaarRef = useRef(messagePaar);
  useEffect(() => {
    if (messagePaar) messagePaarRef.current = messagePaar;
  }, [messagePaar]);

  // Callback ref for the photo/video caption contentEditable. Fires the
  // moment the DOM node attaches, including Strict Mode remounts. Re-seed
  // whenever the node is empty so typed chat text is not dropped.
  const setPhotoEditorRef = React.useCallback((node) => {
    photoRichEditorRef.current = node;
    if (!node) {
      photoEditorInitRef.current = false;
      return;
    }
    const editorEmpty = !String(node.innerText || "").replace(/\u00A0/g, " ").trim();
    if (editorEmpty) {
      const pendingHtml = pendingPhotoHtmlRef.current;
      const seed = messagePaarRef.current;
      if (pendingHtml) {
        node.innerHTML = pendingHtml;
      } else if (seed) {
        node.innerHTML = photoMarkdownToHtml(seed);
        pendingPhotoHtmlRef.current = node.innerHTML;
      }
      if (node.innerHTML) {
        try {
          node.focus();
          const range = document.createRange();
          range.selectNodeContents(node);
          range.collapse(false);
          const sel = window.getSelection();
          sel.removeAllRanges();
          sel.addRange(range);
        } catch (e) { /* ignore selection errors */ }
      }
    }
    photoEditorInitRef.current = true;
  }, [photoMarkdownToHtml]);

  useEffect(() => {
    const editor = photoRichEditorRef.current;
    if (!editor || !messagePaar) return;
    const empty = !String(editor.innerText || "").replace(/\u00A0/g, " ").trim();
    if (!empty) return;
    editor.innerHTML = photoMarkdownToHtml(messagePaar);
    pendingPhotoHtmlRef.current = editor.innerHTML;
  }, [messagePaar, image.length, video, docs.length, photoMarkdownToHtml]);

  const handlePhotoEditorInput = React.useCallback(() => {
    const editor = photoRichEditorRef.current;
    const md = getPhotoRichContent();
    if (editor) pendingPhotoHtmlRef.current = editor.innerHTML;
    // Never push an empty caption back into the main composer — that wipes
    // in-progress text when the preview editor remounts empty.
    if (md) {
      userTyping({ target: { value: md }, currentTarget: { value: md } });
    }
    const sel = window.getSelection();
    if (sel && sel.rangeCount > 0 && editor) {
      const range = sel.getRangeAt(0);
      if (editor.contains(range.startContainer) && range.startContainer.nodeType === Node.TEXT_NODE) {
        const text = range.startContainer.textContent;
        const cursorPos = range.startOffset;
        const textBeforeCursor = text.substring(0, cursorPos);
        const atIdx = textBeforeCursor.lastIndexOf("@");
        if (atIdx >= 0) {
          const chBefore = atIdx > 0 ? textBeforeCursor[atIdx - 1] : null;
          if (atIdx === 0 || chBefore === " " || chBefore === "\n" || chBefore === "\xa0") {
            const query = textBeforeCursor.substring(atIdx + 1);
            if (query.length <= 50) {
              setPhotoMentionInput(query);
              setPhotoShowSuggestions(true);
              photoMentionCursorRef.current = { node: range.startContainer, offset: cursorPos, atIndex: atIdx };
              return;
            }
          }
        }
      }
    }
    setPhotoShowSuggestions(false);
  }, [getPhotoRichContent, userTyping]);

  const onPhotoMentionSelect = React.useCallback((mention) => {
    if (isPhotoWysiwygOpen && photoMentionCursorRef.current) {
      const editor = photoRichEditorRef.current;
      if (editor) {
        const { node, offset, atIndex } = photoMentionCursorRef.current;
        if (node && node.parentNode && editor.contains(node)) {
          const before = node.textContent.substring(0, atIndex);
          const after = node.textContent.substring(offset);
          const mentionSpan = document.createElement("span");
          mentionSpan.className = "mention-tag";
          mentionSpan.setAttribute("data-mention-id", mention.id);
          mentionSpan.setAttribute("data-mention-name", mention.name);
          mentionSpan.contentEditable = "false";
          mentionSpan.textContent = `@${mention.name}`;
          const parent = node.parentNode;
          const beforeText = document.createTextNode(before);
          const afterText = document.createTextNode("\u00A0" + after);
          parent.insertBefore(beforeText, node);
          parent.insertBefore(mentionSpan, node);
          parent.insertBefore(afterText, node);
          parent.removeChild(node);
          const sel = window.getSelection();
          const newRange = document.createRange();
          newRange.setStart(afterText, 1);
          newRange.collapse(true);
          sel.removeAllRanges();
          sel.addRange(newRange);
          const md = getPhotoRichContent();
          userTyping({ target: { value: md }, currentTarget: { value: md } });
        }
      }
      photoMentionCursorRef.current = null;
    }
    setPhotoMentionInput("");
    setPhotoShowSuggestions(false);
  }, [isPhotoWysiwygOpen, getPhotoRichContent, userTyping]);

  const ingestDroppedFiles = (files) => {
    const list = files ? Array.from(files).filter(Boolean) : [];
    if (list.length === 0) return;
    const validImages = [];
    const validDocs = [];
    let videoFile = null;
    let oversizedDocCount = 0;

    for (let index = 0; index < list.length; index++) {
      const file = list[index];
      const fileType = file.name?.split(".").pop()?.toLowerCase();

      if (isPhotoFile(file)) {
        if (!file.url) file.url = URL.createObjectURL(file);
        validImages.push(file);
      } else if (isVideoFile(file) || VIDEO_EXTENSIONS.includes(fileType)) {
        videoFile = file;
      } else if (DOC_EXTENSIONS.includes(fileType)) {
        if (isDocOversized(file)) {
          oversizedDocCount++;
        } else {
          if (!file.url) file.url = URL.createObjectURL(file);
          validDocs.push(file);
        }
      }
    }

    if (oversizedDocCount > 0) {
      $crud.notify({ type: "error", message: "Each document must be 25 MB or smaller." });
    }

    if (validDocs.length > 0 && (validImages.length > 0 || videoFile)) {
      $crud.notify({
        type: "error",
        message: "Please drop either images or documents, not both.",
      });
    } else if (validDocs.length > 0) {
      addDocumentsFromList(validDocs);
    }

    if (validImages.length > 0 && videoFile) {
      $crud.notify({
        type: "error",
        message: strings.video_or_images_only || "Please select either images or a single video.",
      });
      return;
    }

    if (validImages.length > 0) {
      appendImages(validImages);
    }

    if (videoFile) {
      prepareChatVideo(videoFile).then((result) => {
        if (!result.ok) {
          $crud.notify({
            type: "error",
            message: strings[result.errorKey] || result.errorKey,
          });
          return;
        }
        setVideo(result.file);
      });
    }
  };

  useEffect(() => {
    if (!dropfiles || dropfiles.length === 0) {
      lastDropfilesRef.current = null;
      return;
    }
    if (lastDropfilesRef.current === dropfiles) return;
    lastDropfilesRef.current = dropfiles;
    ingestDroppedFiles(dropfiles);
  }, [dropfiles]);

  const handleUploadImageDialog = (e) => {
    const { files } = e.target;
    if (!files || files.length === 0) return;
    const filesArray = Array.from(files);
    let imageFiles = [];
    let videoFiles = [];

    for (const file of filesArray) {
      const fileType = file.name?.split(".").pop()?.toLowerCase();
      if (isVideoFile(file) || VIDEO_EXTENSIONS.includes(fileType)) {
        videoFiles.push(file);
      } else if (isPhotoFile(file)) {
        imageFiles.push(file);
      }
    }

    if (videoFiles.length > 1) {
      setVideo("");
      $crud.notify({ type: "error", message: strings.video_one_at_a_time || "Please select one video file at a time." });
      return;
    } else if (imageFiles.length > 0 && videoFiles.length > 0) {
      setVideo("");
      $crud.notify({ type: "error", message: strings.video_or_images_only || "Please select multiple image or single video file only." });
      return;
    }

    if (videoFiles.length === 1) {
      prepareChatVideo(videoFiles[0]).then((result) => {
        if (!result.ok) {
          $crud.notify({
            type: "error",
            message: strings[result.errorKey] || result.errorKey,
          });
          return;
        }
        setVideo(result.file);
      });
    }

    if (imageFiles.length > 0) {
      appendImages(imageFiles);
    }

    if (imageFiles.length === 0 && videoFiles.length === 0) {
      $crud.notify({ type: "error", message: "Upload image in ['jpg','png','jpeg']" });
    }
  };

  function getHeightWidth(file, index) {
    const url = file.url || URL.createObjectURL(file);
    const img = new Image();
    img.src = url;
    img.onload = function () {
      setImageDimension((prevValue) => ({
        ...prevValue,
        [index]: { width: this.width, height: this.height },
      }));
    };
  }

  const addDocumentsFromList = React.useCallback((fileList) => {
    if (!fileList || fileList.length === 0) return;

    const accepted = [];
    let invalidCount = 0;
    let oversizedCount = 0;

    for (const file of Array.from(fileList)) {
      const fileType = file.name?.split(".").pop()?.toLowerCase();
      if (!DOC_EXTENSIONS.includes(fileType)) {
        invalidCount++;
        continue;
      }
      if (isDocOversized(file)) {
        oversizedCount++;
        continue;
      }
      if (!file.url) file.url = URL.createObjectURL(file);
      accepted.push(file);
    }

    if (invalidCount > 0) {
      $crud.notify({ type: "error", message: `Upload file in [${DOC_EXTENSIONS.join(", ")}] format` });
    }
    if (oversizedCount > 0) {
      $crud.notify({ type: "error", message: "Each document must be 25 MB or smaller." });
    }
    if (accepted.length === 0) return;

    setDocs((prev) => {
      const existingKeys = new Set(prev.map(fileFingerprint));
      const unique = accepted.filter((file) => {
        const key = fileFingerprint(file);
        if (existingKeys.has(key)) return false;
        existingKeys.add(key);
        return true;
      });
      if (unique.length === 0) return prev;
      const merged = [...prev, ...unique];
      if (merged.length > MAX_DOCUMENTS) {
        $crud.notify({
          type: "error",
          message: strings.max_documents_reached || `You can attach up to ${MAX_DOCUMENTS} documents at a time.`,
        });
      }
      return merged.slice(0, MAX_DOCUMENTS);
    });
  }, [strings]);

  const handleUploadDocDialog = (e) => {
    addDocumentsFromList(e.target.files);
  };

  const onDocDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (![...e.dataTransfer.types].includes("Files")) return;
    docDragDepthRef.current += 1;
    setIsDocDragOver(true);
  };

  const onDocDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = "copy";
  };

  const onDocDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    docDragDepthRef.current = Math.max(0, docDragDepthRef.current - 1);
    if (docDragDepthRef.current === 0) setIsDocDragOver(false);
  };

  const onDocDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    docDragDepthRef.current = 0;
    setIsDocDragOver(false);
    addDocumentsFromList(getDroppedFiles(e.dataTransfer));
  };

  const appendImages = (imageFiles) => {
    if (!imageFiles || imageFiles.length === 0) return;
    setImage((prevValue) => {
      const existingKeys = new Set(prevValue.map(fileFingerprint));
      const processed = [];
      for (const file of imageFiles) {
        if (!file) continue;
        const key = fileFingerprint(file);
        if (existingKeys.has(key)) continue;
        existingKeys.add(key);
        if (!file.url) file.url = URL.createObjectURL(file);
        processed.push(file);
      }
      if (processed.length === 0) return prevValue;
      const startIndex = prevValue.length;
      processed.forEach((file, i) => getHeightWidth(file, startIndex + i));
      return [...prevValue, ...processed];
    });
  };

  const onImageDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    docDragDepthRef.current = 0;
    setIsDocDragOver(false);
    const files = getDroppedFiles(e.dataTransfer);
    if (files.length === 0) return;

    const imageFiles = files.filter(isPhotoFile);

    if (imageFiles.length === 0) {
      $crud.notify({ type: "error", message: "Upload image in ['jpg','png','jpeg','gif','webp','bmp','heic']" });
      return;
    }
    appendImages(imageFiles);
  };

  const removeDoc = (index) => {
    setDocs((prev) => {
      const next = prev.filter((_, i) => i !== index);
      if (docActiveIndex >= next.length && next.length > 0) {
        setDocActiveIndex(next.length - 1);
      }
      return next;
    });
  };

  const navigateDoc = (direction) => {
    if (direction === "prev" && docActiveIndex > 0) {
      setDocActiveIndex(docActiveIndex - 1);
    } else if (direction === "next" && docActiveIndex < docs.length - 1) {
      setDocActiveIndex(docActiveIndex + 1);
    }
  };

  const sendDocuments = (chatId) => {
    sendDocFiles(resolveCaption(), docs, chatId);
    handleClose();
  };

  const renderDocPreview = (doc) => {
    if (!doc) return null;
    const kind = getDocKind(doc);
    if (kind === "pdf") {
      return (
        <iframe
          title={doc.name}
          src={doc.url}
          className="floating-action__pdf-frame"
        />
      );
    }
    if (kind === "image") {
      return <img src={doc.url} alt={doc.name} className="photo-preview-image" />;
    }
    if (kind === "spreadsheet") {
      return (
        <ExcelPreview
          src={doc.url}
          fileName={doc.name}
          compact
          loadingLabel={strings.loading || "Loading"}
          failedLabel={strings.no_preview_available || "No preview available"}
          truncatedLabel={strings.spreadsheet_preview_truncated || ""}
        />
      );
    }
    const color = getDocAccentColor(doc);
    const extension = getDocExtension(doc).toUpperCase();
    const size = formatDocSize(doc.size);
    return (
      <div className="doc-no-preview-card">
        <div className="doc-no-preview-icon floating-action__doc-icon-box" style={{ "--doc-bg": `${color}1a`, "--doc-color": color }}>
          <span className="material-symbols-outlined floating-action__doc-icon">
            {getDocIconName(doc)}
          </span>
          {extension ? (
            <span className="doc-no-preview-badge floating-action__doc-badge">
              {extension.slice(0, 5)}
            </span>
          ) : null}
        </div>
        <div className="doc-no-preview-name" title={doc.name}>{doc.name}</div>
        {size ? <div className="doc-no-preview-size">{size}</div> : null}
        <div className="doc-no-preview-title">{strings.no_preview_available || "No preview available"}</div>
        <div className="doc-no-preview-hint">
          {strings.no_preview_hint || "This file type can't be previewed, but it will still be sent."}
        </div>
      </div>
    );
  };

  const renderDocThumb = (doc) => {
    const kind = getDocKind(doc);
    if (kind === "image") {
      return <img src={doc.url} alt="" />;
    }
    const color = getDocAccentColor(doc);
    return (
      <div className="photo-preview-doc-thumb floating-action__doc-thumb" style={{ "--doc-bg": `${color}14`, "--doc-color": color }}>
        <span className="material-symbols-outlined floating-action__doc-thumb-icon">
          {getDocIconName(doc)}
        </span>
        <span className="floating-action__doc-thumb-label">
          {getDocExtension(doc).toUpperCase().slice(0, 4)}
        </span>
      </div>
    );
  };

  const sendMessage = (chatId) => {
    sendImageMessage(resolveCaption(), image, imageDimension, chatId);
    handleClose();
  };

  const handleVideoMessage = (chatId) => {
    sendVideoMessage(video, resolveCaption(), chatId);
    handleClose();
  };

  const onInputClick = (event) => {
    event.target.value = "";
  };

  const removeImage = (index) => {
    setImage(prev => {
      const newImages = prev.filter((_, i) => i !== index);
      if (activeIndex >= newImages.length && newImages.length > 0) {
        setActiveIndex(newImages.length - 1);
      }
      return newImages;
    });
  };

  const navigateImage = (direction) => {
    if (direction === 'prev' && activeIndex > 0) {
      setActiveIndex(activeIndex - 1);
    } else if (direction === 'next' && activeIndex < image.length - 1) {
      setActiveIndex(activeIndex + 1);
    }
  };

  const handleCloseWithAnimation = () => {
    setIsClosing(true);
    setTimeout(() => {
      handleClose();
    }, 200);
  };

  const menuOpen = !image.length && !video && !docs.length;

  // Escape closes the share sheet.
  useEffect(() => {
    if (!menuOpen) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") handleCloseWithAnimation();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [menuOpen]);

  // "Standort" / "Treffpunkt": the conversation's location page, opened in that mode.
  const openLocation = (mode) => {
    if (typeof showLocationDialog !== "function") return;
    requestLocationMode(mode);
    showLocationDialog();
  };

  // Share sheet (mobile ProShareSheet)
  if (menuOpen) {
    const mediaNote = !canSendMedia
      ? (strings.media_send_disabled || (isGerm ? "Vom Administrator deaktiviert." : "Disabled by admin."))
      : null;
    const shareItems = [
      {
        key: "media",
        kind: "media",
        label: strings.chat_share_gallery || (isGerm ? "Galerie" : "Gallery"),
        disabled: !canSendMedia,
        onClick: () => imageInputRef.current && imageInputRef.current.click(),
      },
      {
        key: "document",
        kind: "document",
        label: strings.document || (isGerm ? "Dokument" : "Document"),
        disabled: !canSendMedia,
        onClick: () => docInputRef.current && docInputRef.current.click(),
      },
      showLocationDialog
        ? { key: "location", kind: "location", label: strings.location || (isGerm ? "Standort" : "Location"), disabled: !canSendMedia, onClick: () => openLocation("location") }
        : null,
      showLocationDialog
        ? { key: "meeting", kind: "meeting", label: strings.chat_share_meeting_point || (isGerm ? "Treffpunkt" : "Meeting point"), disabled: !canSendMedia, onClick: () => openLocation("meeting") }
        : null,
      onCreatePoll
        ? { key: "poll", kind: "poll", label: strings.create_poll || (isGerm ? "Umfrage" : "Poll"), onClick: () => { onCreatePoll(); handleCloseWithAnimation(); } }
        : null,
    ];
    // File inputs stop their click: it would bubble to the overlay and close the sheet.
    const stopInputClick = (event) => {
      event.stopPropagation();
      onInputClick(event);
    };
    return (
      <div
        className={`photo-preview-overlay light ${isClosing ? 'closing' : ''} ${isDocDragOver ? 'is-drop-target' : ''}`}
        onClick={handleCloseWithAnimation}
        onDragEnter={onDocDragEnter}
        onDragOver={onDocDragOver}
        onDragLeave={onDocDragLeave}
        onDrop={(e) => {
          e.preventDefault();
          e.stopPropagation();
          docDragDepthRef.current = 0;
          setIsDocDragOver(false);
          ingestDroppedFiles(getDroppedFiles(e.dataTransfer));
        }}
      >
        {isDocDragOver && (
          <div className="photo-preview-drop-hint">
            <span className="material-symbols-outlined">upload_file</span>
            <span>{strings.drop_files_here || (isGerm ? "Dateien hier ablegen" : "Drop files here")}</span>
          </div>
        )}
        <input
          type="file"
          hidden
          multiple
          onChange={handleUploadImageDialog}
          onClick={stopInputClick}
          accept={PHOTO_VIDEO_ACCEPT_ATTRIBUTE}
          ref={imageInputRef}
        />
        <input
          type="file"
          hidden
          multiple
          onChange={handleUploadDocDialog}
          onClick={stopInputClick}
          accept={DOC_ACCEPT_ATTRIBUTE}
          ref={docInputRef}
        />
        <div className="kjc-share-wrap">
          <ChatShareSheet
            title={strings.chat_share_title || (isGerm ? "Teilen" : "Share")}
            items={shareItems}
            note={mediaNote}
            onClose={handleCloseWithAnimation}
            closeLabel={strings.cancel || (isGerm ? "Abbrechen" : "Cancel")}
            isInternal={isInternal}
          />
        </div>
      </div>
    );
  }

  // Document Preview
  if (docs.length > 0) {
    const currentDoc = docs[docActiveIndex];

    return (
      <div
        className={`photo-preview-overlay light ${isInternal ? 'photo-preview-internal' : ''} ${isClosing ? 'closing' : ''} ${isDocDragOver ? 'is-drop-target' : ''}`}
        onDragEnter={onDocDragEnter}
        onDragOver={onDocDragOver}
        onDragLeave={onDocDragLeave}
        onDrop={onDocDrop}
      >
        {isDocDragOver && (
          <div className="photo-preview-drop-hint">
            <span className="material-symbols-outlined">upload_file</span>
            <span>{strings.drop_files_here || (isGerm ? "Dateien hier ablegen" : "Drop files here")}</span>
          </div>
        )}
        <div className="photo-preview-container">
          {/* Header */}
          <div className="photo-preview-header">
            <div className="photo-preview-counter">
              {docs.length > 1 ? `${docActiveIndex + 1} / ${docs.length}` : `1 ${strings.document}`}
            </div>
            <button type="button" className="photo-preview-close-btn kj-btn kj-btn--icon kj-btn--sm kj-btn--neutral" onClick={handleCloseWithAnimation}>
              <IoClose size={16} />
            </button>
          </div>

          {/* Main Preview Area */}
          <div className="photo-preview-main">
            {docs.length > 1 && docActiveIndex > 0 && (
              <button
                className="photo-preview-nav photo-preview-nav-prev kj-btn kj-btn--icon kj-btn--neutral kj-btn--round kj-btn--on-surface kj-btn--raised"
                onClick={() => navigateDoc('prev')}
              >
                <MaterialIcon icon="keyboard_arrow_left" size={24} color="var(--kjuup-accent)" fill={0} weight={300} grade={0} />
              </button>
            )}

            <div className="photo-preview-image-container">
              {renderDocPreview(currentDoc)}
            </div>

            {docs.length > 1 && docActiveIndex < docs.length - 1 && (
              <button
                className="photo-preview-nav photo-preview-nav-next kj-btn kj-btn--icon kj-btn--neutral kj-btn--round kj-btn--on-surface kj-btn--raised"
                onClick={() => navigateDoc('next')}
              >
                <MaterialIcon icon="keyboard_arrow_right" size={24} color="var(--kjuup-accent)" fill={0} weight={300} grade={0} />
              </button>
            )}
          </div>

          {/* Thumbnail Strip */}
          <div className="photo-preview-thumbnails">
            {docs.map((doc, index) => (
              <div
                key={`${doc?.name || 'doc'}-${index}`}
                className={`photo-preview-thumb ${index === docActiveIndex ? 'active' : ''} ${isInternal ? 'photo-preview-thumb-internal' : ''}`}
                onClick={() => setDocActiveIndex(index)}
                title={doc?.name}
              >
                {renderDocThumb(doc)}
                <button
                  className="photo-preview-thumb-delete kj-btn kj-btn--icon kj-btn--sm kj-btn--neutral kj-btn--round kj-btn--on-surface kj-btn--raised"
                  onClick={(e) => {
                    e.stopPropagation();
                    removeDoc(index);
                  }}
                >
                  <IoClose size={12} />
                </button>
              </div>
            ))}

            {docs.length < MAX_DOCUMENTS && (
              <div
                className="photo-preview-thumb photo-preview-add-more"
                onClick={() => docAddMoreInputRef.current.click()}
                title={strings.add_documents || 'Add documents'}
              >
                <input
                  type="file"
                  hidden
                  multiple
                  onChange={handleUploadDocDialog}
                  onClick={onInputClick}
                  accept={DOC_ACCEPT_ATTRIBUTE}
                  ref={docAddMoreInputRef}
                />
                <span className="material-symbols-outlined photo-preview-add-icon">add_circle</span>
              </div>
            )}
          </div>

          {/* Mention suggestions dropdown */}
          {photoShowSuggestions && mentionSuggestions.length > 0 && (() => {
            const filtered = mentionSuggestions.filter((m) => m.name?.toLowerCase().includes(photoMentionInput?.toLowerCase()));
            if (filtered.length === 0) return null;
            return (
              <div className="floating-action__mentions">
                {filtered.map((mention) => (
                  <div
                    key={mention.id}
                    className="photo-mention-suggestion-item"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => onPhotoMentionSelect(mention)}
                  >
                    <img
                      src={mention.type !== "All" && mention.avatar
                        ? BASE_URL + mention.avatar
                        : process.env.PUBLIC_URL + "/assets/img/head.svg"
                      }
                      className="kj-avatar-32"
                      alt=""
                    />
                    <span className="floating-action__mention-name">{mention.name}</span>
                  </div>
                ))}
              </div>
            );
          })()}

          {/* Input Area - Same style as chat */}
          <div className="photo-preview-input-section">
            <div className="photo-preview-input-row">
              <div
                className={`input_text_msg customInputBoxNew photo-preview-input-box ${isInternal ? 'internal-chat-input' : 'external-chat-input'}`}
                onClick={(e) => {
                  if (!isPhotoWysiwygOpen) {
                    if (e.target === e.currentTarget || !e.target.closest('textarea')) {
                      const textarea = e.currentTarget.querySelector('textarea');
                      if (textarea) textarea.focus();
                    }
                  } else {
                    const editor = photoRichEditorRef.current;
                    if (editor && (e.target === e.currentTarget)) editor.focus();
                  }
                }}
              >
                {isPhotoWysiwygOpen && (
                  <div className="chat-wysiwyg-toolbar">
                    <button type="button" className={`chat-wysiwyg-btn${photoActiveFormats.bold ? ' active' : ''}`} title="Bold" onMouseDown={(e) => { e.preventDefault(); applyPhotoFormat("bold"); setTimeout(updatePhotoActiveFormats, 0); }}><MaterialIcon icon="format_bold" size={18} /></button>
                    <button type="button" className={`chat-wysiwyg-btn${photoActiveFormats.italic ? ' active' : ''}`} title="Italic" onMouseDown={(e) => { e.preventDefault(); applyPhotoFormat("italic"); setTimeout(updatePhotoActiveFormats, 0); }}><MaterialIcon icon="format_italic" size={18} /></button>
                    <button type="button" className={`chat-wysiwyg-btn${photoActiveFormats.underline ? ' active' : ''}`} title="Underline" onMouseDown={(e) => { e.preventDefault(); applyPhotoFormat("underline"); setTimeout(updatePhotoActiveFormats, 0); }}><MaterialIcon icon="format_underlined" size={18} /></button>
                    <span className="chat-wysiwyg-separator" />
                    <button type="button" className={`chat-wysiwyg-btn${photoActiveFormats.unordered ? ' active' : ''}`} title="Bullet list" onMouseDown={(e) => { e.preventDefault(); applyPhotoFormat("unordered"); setTimeout(updatePhotoActiveFormats, 0); }}><MaterialIcon icon="format_list_bulleted" size={18} /></button>
                    <button type="button" className={`chat-wysiwyg-btn${photoActiveFormats.ordered ? ' active' : ''}`} title="Numbered list" onMouseDown={(e) => { e.preventDefault(); applyPhotoFormat("ordered"); setTimeout(updatePhotoActiveFormats, 0); }}><MaterialIcon icon="format_list_numbered" size={18} /></button>
                  </div>
                )}
                {isPhotoWysiwygOpen ? (
                  <div
                    ref={setPhotoEditorRef}
                    className="chat-rich-editor"
                    contentEditable
                    suppressContentEditableWarning
                    data-placeholder={isGerm ? "Nachricht hinzufügen..." : "Add a message..."}
                    onPaste={pasteAsPlainText}
                    onDrop={handleComposerDrop}
                    onInput={handlePhotoEditorInput}
                    onBlur={(e) => onBlur(e)}
                  />
                ) : (
                  <textarea
                    placeholder={isGerm ? "Nachricht hinzufügen..." : "Add a message..."}
                    className="photo-preview-textarea"
                    onInput={(e) => userTyping(e)}
                    onBlur={(e) => onBlur(e)}
                    value={messagePaar}
                    rows={1}
                  />
                )}
              </div>

              <a
                href="#"
                onMouseDown={(e) => e.preventDefault()}
                onClick={(e) => {
                  e.preventDefault();
                  sendDocuments(chat.id);
                }}
              >
                <span className="material-symbols-outlined send-icon floating-action__send-icon">send</span>
              </a>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Photo Preview
  if (image.length > 0) {
    const currentImage = image[activeIndex];
    const isHeic = !currentImage?.type || currentImage?.type === 'image/heic' || currentImage?.name?.endsWith('.heic');
    
    return (
      <div
        className={`photo-preview-overlay light ${isInternal ? 'photo-preview-internal' : ''} ${isClosing ? 'closing' : ''} ${isDocDragOver ? 'is-drop-target' : ''}`}
        onDragEnter={onDocDragEnter}
        onDragOver={onDocDragOver}
        onDragLeave={onDocDragLeave}
        onDrop={onImageDrop}
      >
        {isDocDragOver && (
          <div className="photo-preview-drop-hint">
            <span className="material-symbols-outlined">add_photo_alternate</span>
            <span>{strings.drop_files_here || (isGerm ? "Dateien hier ablegen" : "Drop files here")}</span>
          </div>
        )}
        <div className="photo-preview-container">
          {/* Header */}
          <div className="photo-preview-header">
            <div className="photo-preview-counter">
              {image.length > 1 ? `${activeIndex + 1} / ${image.length}` : (isGerm ? '1 Bild' : '1 Image')}
            </div>
            <button type="button" className="photo-preview-close-btn kj-btn kj-btn--icon kj-btn--sm kj-btn--neutral" onClick={handleCloseWithAnimation}>
              <IoClose size={16} />
            </button>
          </div>

          {/* Main Preview Area */}
          <div className="photo-preview-main">
            {/* Navigation Arrows */}
            {image.length > 1 && activeIndex > 0 && (
              <button 
                className="photo-preview-nav photo-preview-nav-prev kj-btn kj-btn--icon kj-btn--neutral kj-btn--round kj-btn--on-surface kj-btn--raised"
                onClick={() => navigateImage('prev')}
              >
                <MaterialIcon icon="keyboard_arrow_left" size={24} color="var(--kjuup-accent)" fill={0} weight={300} grade={0} />
              </button>
            )}
            
            {/* Image Display */}
            <div className="photo-preview-image-container">
              <img
                src={isHeic 
                  ? process.env.PUBLIC_URL + `assets/img/${isGerm ? 'no-image-preview-de.png' : 'no-image-preview-en.png'}`
                  : currentImage?.url
                }
                alt={currentImage?.name || 'Preview'}
                className="photo-preview-image"
              />
            </div>

            {image.length > 1 && activeIndex < image.length - 1 && (
              <button 
                className="photo-preview-nav photo-preview-nav-next kj-btn kj-btn--icon kj-btn--neutral kj-btn--round kj-btn--on-surface kj-btn--raised"
                onClick={() => navigateImage('next')}
              >
                <MaterialIcon icon="keyboard_arrow_right" size={24} color="var(--kjuup-accent)" fill={0} weight={300} grade={0} />
              </button>
            )}
          </div>

          {/* Thumbnail Strip */}
          <div className="photo-preview-thumbnails">
            {image.map((img, index) => {
              const isHeicThumb = !img?.type || img?.type === 'image/heic' || img?.name?.endsWith('.heic');
              return (
                <div
                  key={img?.url || `${img?.name}-${index}`}
                  className={`photo-preview-thumb ${index === activeIndex ? 'active' : ''} ${isInternal ? 'photo-preview-thumb-internal' : ''}`}
                  onClick={() => setActiveIndex(index)}
                >
                  <img
                    src={isHeicThumb
                      ? process.env.PUBLIC_URL + `assets/img/${isGerm ? 'no-image-preview-de.png' : 'no-image-preview-en.png'}`
                      : img?.url
                    }
                    alt=""
                  />
                  {/* Delete button on each thumbnail */}
                  <button 
                    className="photo-preview-thumb-delete kj-btn kj-btn--icon kj-btn--sm kj-btn--neutral kj-btn--round kj-btn--on-surface kj-btn--raised"
                    onClick={(e) => {
                      e.stopPropagation();
                      removeImage(index);
                    }}
                  >
                    <IoClose size={12} />
                  </button>
                </div>
              );
            })}
            
            {/* Add More Button */}
            <div 
              className="photo-preview-thumb photo-preview-add-more"
              onClick={() => addMoreInputRef.current.click()}
            >
              <input
                type="file"
                hidden
                multiple
                onChange={handleUploadImageDialog}
                onClick={onInputClick}
                accept=".jpg,.jpeg,.png,.gif,.bmp,.webp,.heic,.HEIC"
                ref={addMoreInputRef}
              />
              <span className="material-symbols-outlined photo-preview-add-icon">add_circle</span>
            </div>
          </div>

          {/* Mention suggestions dropdown */}
          {photoShowSuggestions && mentionSuggestions.length > 0 && (() => {
            const filtered = mentionSuggestions.filter((m) => m.name?.toLowerCase().includes(photoMentionInput?.toLowerCase()));
            if (filtered.length === 0) return null;
            return (
              <div className="floating-action__mentions">
                {filtered.map((mention) => (
                  <div
                    key={mention.id}
                    className="photo-mention-suggestion-item"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => onPhotoMentionSelect(mention)}
                  >
                    <img
                      src={mention.type !== "All" && mention.avatar
                        ? BASE_URL + mention.avatar
                        : process.env.PUBLIC_URL + "/assets/img/head.svg"
                      }
                      className="kj-avatar-32"
                      alt=""
                    />
                    <span className="floating-action__mention-name">{mention.name}</span>
                  </div>
                ))}
              </div>
            );
          })()}

          {/* Input Area - Same style as chat */}
          <div className="photo-preview-input-section">
            <div className="photo-preview-input-row">
              <div
                className={`input_text_msg customInputBoxNew photo-preview-input-box ${isInternal ? 'internal-chat-input' : 'external-chat-input'}`}
                onClick={(e) => {
                  if (!isPhotoWysiwygOpen) {
                    if (e.target === e.currentTarget || !e.target.closest('textarea')) {
                      const textarea = e.currentTarget.querySelector('textarea');
                      if (textarea) textarea.focus();
                    }
                  } else {
                    const editor = photoRichEditorRef.current;
                    if (editor && (e.target === e.currentTarget)) editor.focus();
                  }
                }}
              >
                {isPhotoWysiwygOpen && (
                  <div className="chat-wysiwyg-toolbar">
                    <button type="button" className={`chat-wysiwyg-btn${photoActiveFormats.bold ? ' active' : ''}`} title="Bold" onMouseDown={(e) => { e.preventDefault(); applyPhotoFormat("bold"); setTimeout(updatePhotoActiveFormats, 0); }}><MaterialIcon icon="format_bold" size={18} /></button>
                    <button type="button" className={`chat-wysiwyg-btn${photoActiveFormats.italic ? ' active' : ''}`} title="Italic" onMouseDown={(e) => { e.preventDefault(); applyPhotoFormat("italic"); setTimeout(updatePhotoActiveFormats, 0); }}><MaterialIcon icon="format_italic" size={18} /></button>
                    <button type="button" className={`chat-wysiwyg-btn${photoActiveFormats.underline ? ' active' : ''}`} title="Underline" onMouseDown={(e) => { e.preventDefault(); applyPhotoFormat("underline"); setTimeout(updatePhotoActiveFormats, 0); }}><MaterialIcon icon="format_underlined" size={18} /></button>
                    <span className="chat-wysiwyg-separator" />
                    <button type="button" className={`chat-wysiwyg-btn${photoActiveFormats.unordered ? ' active' : ''}`} title="Bullet list" onMouseDown={(e) => { e.preventDefault(); applyPhotoFormat("unordered"); setTimeout(updatePhotoActiveFormats, 0); }}><MaterialIcon icon="format_list_bulleted" size={18} /></button>
                    <button type="button" className={`chat-wysiwyg-btn${photoActiveFormats.ordered ? ' active' : ''}`} title="Numbered list" onMouseDown={(e) => { e.preventDefault(); applyPhotoFormat("ordered"); setTimeout(updatePhotoActiveFormats, 0); }}><MaterialIcon icon="format_list_numbered" size={18} /></button>
                  </div>
                )}
                {isPhotoWysiwygOpen ? (
                  <div
                    ref={setPhotoEditorRef}
                    className="chat-rich-editor"
                    contentEditable
                    suppressContentEditableWarning
                    data-placeholder={isGerm ? "Nachricht hinzufügen..." : "Add a message..."}
                    onPaste={pasteAsPlainText}
                    onDrop={handleComposerDrop}
                    onInput={handlePhotoEditorInput}
                    onBlur={(e) => onBlur(e)}
                  />
                ) : (
                  <textarea
                    placeholder={isGerm ? "Nachricht hinzufügen..." : "Add a message..."}
                    className="photo-preview-textarea"
                    onInput={(e) => userTyping(e)}
                    onBlur={(e) => onBlur(e)}
                    value={messagePaar}
                    rows={1}
                  />
                )}
              </div>
              
              <a
                href="#"
                onMouseDown={(e) => e.preventDefault()}
                onClick={(e) => {
                  e.preventDefault();
                  sendMessage(chat.id);
                }}
              >
                <span className="material-symbols-outlined send-icon floating-action__send-icon">send</span>
              </a>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (video) {
    return (
      <div className={`photo-preview-overlay light ${isInternal ? 'photo-preview-internal' : ''} ${isClosing ? 'closing' : ''}`}>
        <div className="photo-preview-container video-preview">
          <div className="photo-preview-header">
            <div className="photo-preview-counter">
              Video
              {(video.duration || video.size) ? (
                <span className="video-preview-meta">
                  {[
                    video.duration ? formatVideoDuration(video.duration) : null,
                    video.size ? formatDocSize(video.size) : null,
                  ].filter(Boolean).join(" · ")}
                </span>
              ) : null}
            </div>
            <button type="button" className="photo-preview-close-btn kj-btn kj-btn--icon kj-btn--sm kj-btn--neutral" onClick={handleCloseWithAnimation}>
              <IoClose size={16} />
            </button>
          </div>

          <div className="video-preview-main">
            <video
              src={video.url}
              controls
              className="video-preview-player"
            />
          </div>

          {/* Mention suggestions dropdown for video */}
          {photoShowSuggestions && mentionSuggestions.length > 0 && (() => {
            const filtered = mentionSuggestions.filter((m) => m.name?.toLowerCase().includes(photoMentionInput?.toLowerCase()));
            if (filtered.length === 0) return null;
            return (
              <div className="floating-action__mentions">
                {filtered.map((mention) => (
                  <div
                    key={mention.id}
                    className="photo-mention-suggestion-item"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => onPhotoMentionSelect(mention)}
                  >
                    <img
                      src={mention.type !== "All" && mention.avatar
                        ? BASE_URL + mention.avatar
                        : process.env.PUBLIC_URL + "/assets/img/head.svg"
                      }
                      className="kj-avatar-32"
                      alt=""
                    />
                    <span className="floating-action__mention-name">{mention.name}</span>
                  </div>
                ))}
              </div>
            );
          })()}

          <div className="photo-preview-input-section">
            <div className="photo-preview-input-row">
              <div
                className={`input_text_msg customInputBoxNew photo-preview-input-box ${isInternal ? 'internal-chat-input' : 'external-chat-input'}`}
                onClick={(e) => {
                  if (!isPhotoWysiwygOpen) {
                    if (e.target === e.currentTarget || !e.target.closest('textarea')) {
                      const textarea = e.currentTarget.querySelector('textarea');
                      if (textarea) textarea.focus();
                    }
                  } else {
                    const editor = photoRichEditorRef.current;
                    if (editor && (e.target === e.currentTarget)) editor.focus();
                  }
                }}
              >
                {isPhotoWysiwygOpen && (
                  <div className="chat-wysiwyg-toolbar">
                    <button type="button" className={`chat-wysiwyg-btn${photoActiveFormats.bold ? ' active' : ''}`} title="Bold" onMouseDown={(e) => { e.preventDefault(); applyPhotoFormat("bold"); setTimeout(updatePhotoActiveFormats, 0); }}><MaterialIcon icon="format_bold" size={18} /></button>
                    <button type="button" className={`chat-wysiwyg-btn${photoActiveFormats.italic ? ' active' : ''}`} title="Italic" onMouseDown={(e) => { e.preventDefault(); applyPhotoFormat("italic"); setTimeout(updatePhotoActiveFormats, 0); }}><MaterialIcon icon="format_italic" size={18} /></button>
                    <button type="button" className={`chat-wysiwyg-btn${photoActiveFormats.underline ? ' active' : ''}`} title="Underline" onMouseDown={(e) => { e.preventDefault(); applyPhotoFormat("underline"); setTimeout(updatePhotoActiveFormats, 0); }}><MaterialIcon icon="format_underlined" size={18} /></button>
                    <span className="chat-wysiwyg-separator" />
                    <button type="button" className={`chat-wysiwyg-btn${photoActiveFormats.unordered ? ' active' : ''}`} title="Bullet list" onMouseDown={(e) => { e.preventDefault(); applyPhotoFormat("unordered"); setTimeout(updatePhotoActiveFormats, 0); }}><MaterialIcon icon="format_list_bulleted" size={18} /></button>
                    <button type="button" className={`chat-wysiwyg-btn${photoActiveFormats.ordered ? ' active' : ''}`} title="Numbered list" onMouseDown={(e) => { e.preventDefault(); applyPhotoFormat("ordered"); setTimeout(updatePhotoActiveFormats, 0); }}><MaterialIcon icon="format_list_numbered" size={18} /></button>
                  </div>
                )}
                {isPhotoWysiwygOpen ? (
                  <div
                    ref={setPhotoEditorRef}
                    className="chat-rich-editor"
                    contentEditable
                    suppressContentEditableWarning
                    data-placeholder={isGerm ? "Nachricht hinzufügen..." : "Add a message..."}
                    onPaste={pasteAsPlainText}
                    onDrop={handleComposerDrop}
                    onInput={handlePhotoEditorInput}
                    onBlur={(e) => onBlur(e)}
                  />
                ) : (
                  <textarea
                    placeholder={isGerm ? "Nachricht hinzufügen..." : "Add a message..."}
                    className="photo-preview-textarea"
                    onInput={(e) => userTyping(e)}
                    onBlur={(e) => onBlur(e)}
                    value={messagePaar}
                    rows={1}
                  />
                )}
              </div>
              
              <a
                href="#"
                onMouseDown={(e) => e.preventDefault()}
                onClick={(e) => {
                  e.preventDefault();
                  handleVideoMessage(chat.id);
                }}
              >
                <span className="material-symbols-outlined send-icon floating-action__send-icon">send</span>
              </a>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return null;
}
